import React from 'react';
import { P } from '@frontend/shadcn/components/typography/p';
import { H3 } from '@frontend/shadcn/components/typography/h3';
import { IField } from '@frontend/app/containers/Projects/LandingPages/types';
import { FieldType } from '@frontend/app/types/globalTypes';
import { isArray } from 'lodash';
import { CalendarIcon } from '@revfluence/fresh-icons/solid/esm';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@frontend/shadcn/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@frontend/shadcn/components/ui/popover';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Calendar } from '@frontend/shadcn/components/ui/calendar';
import { Input } from '@frontend/shadcn/components/ui/input';
import { cn } from '@frontend/shadcn/lib/utils';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Form as FormRoot,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from '@frontend/shadcn/components/ui/form';
import xss from 'xss';
import { ViewMode } from '../constants';
import { MultiSelect } from './Amplify/MultiSelect';

interface FormProps {
  view: ViewMode;
  logo: string;
  programName: string;
  brandName: string;
  fields: IField[];
  title: string;
  description: string;
  supportEmail: string;
  primaryButtonLabel: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSubmit?: (values: Record<string, any>) => void;
}

// Helper to generate Zod schema and default values from IField[]
const generateFormSchemaAndDefaults = (fields: IField[] = []) => {
  const schemaShape: Record<string, z.ZodTypeAny> = {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const defaultValues: Record<string, any> = {};

  fields.forEach((field) => {
    const fieldName = field.name || field.dbName || String(field.id);
    let baseSchema: z.ZodTypeAny;

    switch (FieldType[field.type as keyof typeof FieldType]) {
      case FieldType.TEXT:
      case FieldType.EMAIL:
        baseSchema = z.string();
        defaultValues[fieldName] = '';
        if (field.required) {
          schemaShape[fieldName] = (baseSchema as z.ZodString).min(1, {
            message: `${field.label || fieldName} is required.`,
          });
        } else {
          schemaShape[fieldName] = baseSchema.optional();
        }
        break;
      case FieldType.NUMBER:
      case FieldType.PERCENTAGE:
        baseSchema = z.coerce.number();
        defaultValues[fieldName] = undefined;
        if (field.required) {
          schemaShape[fieldName] = z.coerce
            .number({ required_error: `${field.label || fieldName} is required.` })
            .min(0);
        } else {
          schemaShape[fieldName] = baseSchema.optional();
        }
        break;
      case FieldType.BOOLEAN:
        baseSchema = z.boolean();
        defaultValues[fieldName] = undefined;
        if (field.required) {
          schemaShape[fieldName] = z.boolean({ required_error: `${field.label || fieldName} is required.` });
        } else {
          schemaShape[fieldName] = baseSchema.optional();
        }
        break;
      case FieldType.DATE:
      case FieldType.ANNUAL:
        baseSchema = z.date();
        defaultValues[fieldName] = null;
        if (field.required) {
          schemaShape[fieldName] = z.date({ required_error: `${field.label || fieldName} is required.` });
        } else {
          schemaShape[fieldName] = baseSchema.nullable().optional();
        }
        break;
      case FieldType.ARRAY:
        baseSchema = z.array(z.string());
        defaultValues[fieldName] = [];
        if (field.required) {
          schemaShape[fieldName] = (baseSchema as z.ZodArray<z.ZodString>).nonempty({
            message: `${field.label || fieldName} requires at least one selection.`,
          });
        } else {
          schemaShape[fieldName] = baseSchema.optional();
        }
        break;
      default:
        if (isArray(field.choices) && field.choices.length > 0) {
          baseSchema = z.string();
          defaultValues[fieldName] = '';
          if (field.required) {
            schemaShape[fieldName] = (baseSchema as z.ZodString).min(1, {
              message: `${field.label || fieldName} is required.`,
            });
          } else {
            schemaShape[fieldName] = baseSchema.optional();
          }
        } else {
          schemaShape[fieldName] = z.string().optional();
          defaultValues[fieldName] = '';
        }
    }
  });

  return {
    schema: z.object(schemaShape),
    defaultValues,
  };
};

const getPlaceholderForFieldNameFromConfig = (name: string, required?: boolean) => {
  return `${name}${required ? ' *' : ''}`;
};

export const Form = ({
  view,
  logo,
  programName,
  brandName,
  fields,
  title,
  description,
  supportEmail,
  primaryButtonLabel,
  onSubmit,
}: FormProps) => {
  const isDesktop = view === ViewMode.DESKTOP;

  const { schema, defaultValues } = React.useMemo(() => generateFormSchemaAndDefaults(fields), [fields]);

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const handleFormSubmit = (values: z.infer<typeof schema>) => {
    if (onSubmit) {
      onSubmit(values);
    }
  };

  return (
    <div className="flex p-6 gap-4 flex-col">
      <div className="flex flex-row gap-2 items-center">
        <img src={logo} alt="form" className="w-8 h-8 rounded-lg object-cover" />
        <div className="flex flex-col">
          <P className="text-xs font-medium text-grey-6" fontWeight="font-medium">
            {programName}
          </P>
          <P className="text-xs font-normal text-grey-5">{brandName}</P>
        </div>
      </div>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col">
          <H3 className="text-base font-medium text-grey-6 mb-0">{title}</H3>
          <div
            className="text-sm font-normal text-grey-5 mb-0"
            dangerouslySetInnerHTML={{ __html: xss(description) }}
          />
        </div>
      </div>
      <FormRoot {...form}>
        <form onSubmit={form.handleSubmit(handleFormSubmit)} className="flex flex-col gap-4">
          {fields.map((fieldConfig) => {
            const fieldName = fieldConfig.name || fieldConfig.dbName || String(fieldConfig.id);
            const fieldTypeEnum = FieldType[fieldConfig.type as keyof typeof FieldType];

            return (
              <FormField
                control={form.control}
                key={fieldConfig.id}
                name={fieldName}
                render={({ field: formHookField }) => (
                  <FormItem>
                    <FormControl>
                      {(() => {
                        if (
                          fieldTypeEnum === FieldType.ARRAY &&
                          isArray(fieldConfig.choices) &&
                          fieldConfig.choices.length > 0
                        ) {
                          return (
                            <MultiSelect
                              options={fieldConfig.choices.map((choice) => ({ label: choice, value: choice }))}
                              value={formHookField.value || []}
                              onChange={formHookField.onChange}
                              placeholder={getPlaceholderForFieldNameFromConfig(
                                fieldConfig.label || fieldName,
                                fieldConfig.required,
                              )}
                            />
                          );
                        }
                        if (isArray(fieldConfig.choices) && fieldConfig.choices.length > 0) {
                          return (
                            <Select
                              onValueChange={formHookField.onChange}
                              defaultValue={formHookField.value as string | undefined}
                              value={formHookField.value as string | undefined}
                              disabled={false}
                              name={formHookField.name}
                            >
                              <SelectTrigger className="w-full h-12 bg-white data-[placeholder]:text-grey-4">
                                <SelectValue
                                  placeholder={getPlaceholderForFieldNameFromConfig(
                                    fieldConfig.label || fieldName,
                                    fieldConfig.required,
                                  )}
                                />
                              </SelectTrigger>
                              <SelectContent>
                                {fieldConfig.choices.map((choice) => (
                                  <SelectItem value={choice} key={choice}>
                                    {choice}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          );
                        } else {
                          switch (fieldTypeEnum) {
                            case FieldType.BOOLEAN:
                              return (
                                <Select
                                  onValueChange={(value) => formHookField.onChange(value === 'yes')}
                                  value={
                                    formHookField.value === undefined ? undefined : formHookField.value ? 'yes' : 'no'
                                  }
                                  disabled={false}
                                  name={formHookField.name}
                                >
                                  <SelectTrigger className="w-full h-12 bg-white data-[placeholder]:text-grey-4">
                                    <SelectValue
                                      placeholder={getPlaceholderForFieldNameFromConfig(
                                        fieldConfig.label || fieldName,
                                        fieldConfig.required,
                                      )}
                                    />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="yes">Yes</SelectItem>
                                    <SelectItem value="no">No</SelectItem>
                                  </SelectContent>
                                </Select>
                              );
                            case FieldType.DATE:
                            case FieldType.ANNUAL:
                              return (
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <Button
                                      variant="outline"
                                      className={cn(
                                        'w-full h-12 justify-start text-left font-normal bg-white',
                                        !formHookField.value && 'text-grey-4',
                                      )}
                                      disabled={false}
                                    >
                                      <CalendarIcon className="mr-2 h-4 w-4" />
                                      {formHookField.value instanceof Date ? (
                                        formHookField.value.toLocaleDateString()
                                      ) : (
                                        <span>
                                          {getPlaceholderForFieldNameFromConfig(
                                            fieldConfig.label || fieldName,
                                            fieldConfig.required,
                                          )}
                                        </span>
                                      )}
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-auto p-0">
                                    <Calendar
                                      mode="single"
                                      selected={formHookField.value instanceof Date ? formHookField.value : undefined}
                                      onSelect={formHookField.onChange}
                                      initialFocus
                                      disabled={false}
                                    />
                                  </PopoverContent>
                                </Popover>
                              );
                            case FieldType.TEXT:
                            case FieldType.EMAIL:
                            case FieldType.NUMBER:
                            case FieldType.PERCENTAGE:
                            default:
                              const inputType =
                                fieldTypeEnum === FieldType.EMAIL
                                  ? 'email'
                                  : fieldTypeEnum === FieldType.NUMBER || fieldTypeEnum === FieldType.PERCENTAGE
                                    ? 'number'
                                    : 'text';
                              return (
                                <div className="relative">
                                  <Input
                                    type={inputType}
                                    disabled={false}
                                    placeholder={getPlaceholderForFieldNameFromConfig(
                                      fieldConfig.label || fieldName,
                                      fieldConfig.required,
                                    )}
                                    {...formHookField}
                                    className={cn('w-full h-12 bg-white')}
                                  />
                                </div>
                              );
                          }
                        }
                      })()}
                    </FormControl>
                    <FormDescription>
                      {/* Placeholder for field description, if available in fieldConfig */}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            );
          })}
          {supportEmail && (
            <P className="text-sm font-normal text-grey-5 mb-0">
              Questions? Reach out to <span className="underline">{supportEmail}</span>
            </P>
          )}
          <Button
            type="submit"
            className={cn('w-full py-3 rounded-lg text-lg font-semibold mt-2', !isDesktop && 'py-2 text-base')}
            disabled={form.formState.isSubmitting}
          >
            {form.formState.isSubmitting ? 'Submitting...' : primaryButtonLabel}
          </Button>
        </form>
      </FormRoot>
    </div>
  );
};
